import { useState } from 'react';
import { IconCheck, IconCoin } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, useToast } from '@hana/ui';
import { MAT_ROWS, matChosen, matConf, matStatus, type MatPick, type MatTone } from './rcp';
import s from './ar.module.css';
import './match.i18n';

// Mint-AR › tab "Hàng chờ khớp tiền" (1536). TIỀN → chỉ khi có quyền `money`; chỉ đọc: chọn debit chỉ đổi trạng thái tại chỗ, nút ghi báo toast.
const nf = (x: number): string => x.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const TONE: Readonly<Record<MatTone, string>> = { ok: s.fOk ?? '', warn: s.fWarn ?? '', pri: s.kPri ?? '' };
const HEAD: readonly (readonly [string, boolean])[] = [['mt.hDate', false], ['mt.hAmt', true], ['mt.hMemo', false], ['mt.hMatch', false], ['mt.hStatus', false]];

export function MatchQueue({ money }: { money: boolean }) {
  const t = useT();
  const toast = useToast();
  const [pick, setPick] = useState<Readonly<Record<number, MatPick>>>({});
  const [done, setDone] = useState<Readonly<Record<number, boolean>>>({});
  const [pop, setPop] = useState<number | null>(null);
  const [paste, setPaste] = useState('');
  const m = (x: number) => (money ? nf(x) : '•••••');
  const noWrite = () => toast({ msg: t('mt.noWrite') });
  const isDone = (i: number) => MAT_ROWS[i]!.kind === 'done' || !!done[i];
  const cnt = (f: (k: string) => boolean) => MAT_ROWS.filter((r) => f(r.kind)).length;

  return (
    <>
      <section className={s.pasteBox} aria-label={t('mt.pasteAria')}>
        <label className={s.paste}><span>Ask HANA</span><input aria-label={t('mt.pasteInAria')} placeholder={t('mt.pasteIn')} value={paste} onChange={(e) => setPaste(e.target.value)} /></label>
        <button type="button" className={s.pasteGo} onClick={noWrite}>{t('mt.read')}</button>
      </section>
      <div className={s.kpi3}>
        {([['mt.kToday', MAT_ROWS.length, ''], ['mt.kAuto', cnt((k) => k === 'auto' || k === 'done'), s.ok], ['mt.kPick', MAT_ROWS.filter((r, i) => (r.kind === 'pick' || r.kind === 'guess') && !done[i]).length, s.warn]] as const).map(([k, v, c]) => (
          <div key={k}><div className={s.lb}>{t(k)}</div><div><b className={c ?? ''}>{v}</b> <span className={s.lb}>{t('mt.unitRow')}</span></div></div>
        ))}
      </div>
      <section className={s.card} aria-label={t('mt.aria')}>
        <table className={`${s.tbl} ${s.mat}`}>
          <thead><tr>{HEAD.map(([l, right]) => <th key={l} className={right ? s.r : ''}>{t(l)}</th>)}<th /></tr></thead>
          <tbody>
            {MAT_ROWS.map((r, i) => {
              const d = isDone(i);
              const c = matChosen(r, pick[i]);
              const adv = c === 'adv';
              const st = matStatus(r, pick[i], d);
              const sugg = adv ? t('mt.adv') : c >= 0 ? `${r.opts[c]![0]} · ${r.payerShort}` : t('mt.choose');
              return (
                <tr key={r.date + r.amt} className={d ? s.rowDone : ''}>
                  <td>{r.date}<div className={s.sale}>{r.bank}</div></td>
                  <td className={s.r}><b>{m(r.amt)}</b></td>
                  <td title={r.memo}>{r.memo}<div className={s.sale}>{r.payer}</div></td>
                  <td className={s.pickCell}>
                    <button type="button" className={`${s.pick} ${c === -1 && !d ? s.pickWarn : ''}`} aria-haspopup="listbox" aria-expanded={pop === i} onClick={() => { if (!d) setPop(pop === i ? null : i); }}>{sugg} <span className={s.lb}>▾</span></button>
                    <div className={`${s.sale} ${r.kind === 'guess' ? s.warnTx : ''}`}>{matConf(r, d)}</div>
                    {pop === i && (
                      <div role="listbox" aria-label={t('mt.listAria')} className={s.pop}>
                        <div className={s.popHd}>{t('mt.popHd', { p: r.payerShort })}</div>
                        {r.opts.map(([k, v], j) => (
                          <button key={k} type="button" role="option" aria-selected={c === j} className={c === j ? s.optOn : ''} onClick={() => { setPick({ ...pick, [i]: j }); setPop(null); }}><span>{k}</span><span className={s.sale}>{t('mt.left', { v: m(v) })}</span></button>
                        ))}
                        <button type="button" className={s.adv} onClick={() => { setPick({ ...pick, [i]: 'adv' }); setPop(null); }}><IconCoin size={16} aria-hidden="true" />{t('mt.advBtn')}</button>
                      </div>
                    )}
                  </td>
                  <td><span className={`${s.flag} ${TONE[st.tone]}`}>{st.label}</span></td>
                  <td className={s.r}>
                    {!d && c !== -1 && <Button variant="primary" size="sm" icon={<IconCheck size={16} aria-hidden="true" />} onClick={() => { setDone({ ...done, [i]: true }); toast({ msg: t('mt.toastOne', { a: m(r.amt), to: adv ? t('mt.toAdv', { p: r.payerShort }) : r.opts[c]![0] }) }); }}>{t('mt.confirm')}</Button>}
                    {d && <span className={`${s.ok} ${s.okTx}`}><IconCheck size={14} aria-hidden="true" /> {t('mt.done')}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={s.foot}>
          <span>{t('mt.stmt')}</span>
          <button type="button" className={s.act} onClick={() => { const n: Record<number, boolean> = { ...done }; MAT_ROWS.forEach((r, i) => { if (r.kind === 'auto' && pick[i] === undefined) n[i] = true; }); setDone(n); toast({ msg: t('mt.toastAll') }); }}><IconCheck size={16} aria-hidden="true" />{t('mt.confirmAll')}</button>
        </div>
      </section>
    </>
  );
}
